import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Tag } from '@jouzen/ecom-components/v3/Tag';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import { TESTIMONIAL_CARDS } from '@/app/[locale]/membership/_data/TestimonialData';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

export interface QuoteCardsProps {
  readonly tag: MessageKey;
  readonly quote: MessageKey;
  readonly name: string;
  readonly personsPhoto: string;
  readonly photoAlt: MessageKey;
}

export const QuoteCard = ({
  tag,
  quote,
  name,
  personsPhoto,
  photoAlt,
}: QuoteCardsProps) => {
  const t = useTranslations();
  return (
    <div
      className="w-full rounded-2xl bg-sandstone-400 p-6 md:p-7 lg:p-10"
      data-cy="testimonial-quote-card-container"
    >
      <div className="pb-6 md:pb-12" data-cy="testimonial-quote-card-tag">
        <Tag variant="darkSandstone" size="small">
          {t(tag)}
        </Tag>
      </div>
      <blockquote>
        <Typography
          className="pb-6 text-body-lg leading-tight before:block before:content-[open-quote] after:content-[close-quote] md:text-heading-sm"
          color="inherit"
          Element="p"
          font="serif"
          weight="thin"
          data-cy="testimonial-quote-card-quote"
        >
          <RichText messageKey={quote} />
        </Typography>

        <footer className="flex flex-row items-center">
          <Image
            src={personsPhoto}
            alt={photoAlt}
            width={60}
            height={60}
            className="fit h-8 w-8 rounded-full lg:h-15 lg:w-15"
            data-cy="testimonial-quote-card-image"
          />
          <Typography
            className="text-body-small pl-4 font-bold lg:text-xl"
            color="inherit"
            font="sans"
            data-cy="testimonial-quote-card-name"
          >
            {name}
          </Typography>
        </footer>
      </blockquote>
    </div>
  );
};

export const Testimonials = () => {
  return (
    <section data-cy="testimonials">
      <LayoutGrid
        className="gap-y-0 py-10 md:py-20"
        data-cy="testimonials-layout-grid"
      >
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <Typography
            variant="h1"
            Element="h2"
            align="center"
            font="serif"
            className="pt-10 pb-6 leading-tighter tracking-tighter md:pt-20 md:pb-10"
            data-cy="testimonials-title"
          >
            <RichText messageKey="membership_real_members" />
          </Typography>
        </GridItem>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <div
            className="flex flex-col items-stretch gap-6 py-6 md:flex-row md:gap-7.5 md:py-20"
            data-cy="testimonial-quote-cards-container"
          >
            {TESTIMONIAL_CARDS.map((card) => {
              return (
                <QuoteCard
                  tag={card.tag}
                  quote={card.quote}
                  name={card.name}
                  personsPhoto={card.personsPhoto}
                  photoAlt={card.photoAlt}
                  key={card.name}
                />
              );
            })}
          </div>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};
